/**
 * Switching organizations replaces the entire app context: React Query caches,
 * Zustand stores, socket rooms and the API client's organization headers are
 * all scoped to a single organization.
 *
 * Doing that in place used to mean three things racing each other — a store
 * write, an SPA navigation, and a follow-up `window.location.reload()` from
 * `useOrganizationQueryInvalidation`. When the reload won, it re-loaded the
 * *previous* organization's URL (the router had not committed the new one yet)
 * and the switch was silently dropped, so the org had to be picked twice.
 *
 * Handing off with a single document load is both cheaper (one page load
 * instead of a navigation plus a reload) and deterministic: the new document
 * boots directly into the target organization, and every org-scoped store and
 * cache is rebuilt from scratch, so no per-store cleanup is needed at the call
 * site.
 *
 * DELIBERATE — DO NOT REPLACE WITH IN-APP NAVIGATION. The page load on
 * selecting or switching an organization is a product decision, not a
 * smoothness bug to optimize away: no `navigate()`, `history.pushState` or
 * store-swap version of this, and every org selection surface (the
 * /organizations page and the header OrganizationSwitcher) goes through this
 * function. organization-switch.test.ts enforces both; see "Organization
 * switching" in AGENTS.md.
 */
export function handOffToOrganization(
  organizationId: string,
  path: string,
): void {
  // Boot-time priming (socket room, `usePopupOrganizationInit`) reads this
  // before the route resolves, so it must already point at the target org.
  localStorage.setItem('currentOrganizationId', organizationId)
  window.location.assign(path)
}
